import { createRoot } from 'react-dom/client';
import React from 'react';
import { addManifest, registerServiceWorker, runningAsPwa, shouldRunAsPwa } from "./pwa";
import { jwtDecode } from "jwt-decode";
import PWAInstaller from "./pwaInstaller";
import PWAUnderConstruction from "./pwaUnderConstruction";
import PWAUninstaller from "./pwaUninstaller";
import './style.scss';
import { logAppLoadSection } from './appLoadUtils';

logAppLoadSection('base', 'started');

declare global {
  interface Window {
    debugConsoleLog?: (message: string, level?: string) => void;
    fetchDebugLogs?: () => any;
    hchbNavigate?: (path: string) => void;
    __pendingHchbNavigatePath__?: string;
  }
}

const cachedDebugLogs = [];
window.debugConsoleLog = (message: string, level = 'log') => {
  const log = {
    timestamp: new Date(),
    message,
    level
  };
  cachedDebugLogs.push(log);
}

window.fetchDebugLogs = () => {
  const logs = [...cachedDebugLogs];
  cachedDebugLogs.length = 0;
  return logs;
}

// Placeholder for hchbNavigate to store the path if called before navigation is ready
window.hchbNavigate = (path: string) => {
  window.__pendingHchbNavigatePath__ = path;
};

const root = createRoot(document.getElementById('app'));

function isEndpoint(endpoint: string): boolean {
  return window.location.pathname.includes(endpoint);
}

// cache the oidcClient and settings so we can reuse them if the service worker is updated
// also wraps the postMessage call to the service worker
let _props = null;
const TOKEN_REFRESH_DELTA_MS = 60_000; // refresh 1 minute before expiration
let _tokenRefreshTimeoutId: number | null = null;
const clearTokenRefreshTimeout = () => {
  if (_tokenRefreshTimeoutId !== null) {
    clearTimeout(_tokenRefreshTimeoutId);
    _tokenRefreshTimeoutId = null;
  }
};
function getTokenExpirationMs(token: string): number | null {
  try {
    const payload = jwtDecode<{ exp?: number }>(token);
    if (!payload || typeof payload.exp !== 'number') { return null; }
    return payload.exp * 1000; // exp is seconds
  } catch {
    return null;
  }
}
async function scheduleNextTokenRefresh() {
  if (!_props) { return; }
  try {
    const token = await _props.oidcClient.token();
    const expMs = getTokenExpirationMs(token);
    if (!expMs) { return; }
    const now = Date.now();
    const delay = Math.max(5_000, expMs - now - TOKEN_REFRESH_DELTA_MS);
    clearTokenRefreshTimeout();
    _tokenRefreshTimeoutId = window.setTimeout(sendTokenRefreshEvent, delay);
    window.debugConsoleLog?.(`Scheduled token refresh in ${Math.floor(delay / 1000)}s`);
  } catch (ex) {
    window.debugConsoleLog?.(`Failed to schedule token refresh: ${ex}`, 'error');
  }
}
async function sendAppLoadEvent(props?: { oidcClient: any, settings: any }): Promise<void> {
  if (props) {
    _props = props;
  }

  if (!_props) {
    return;
  }

  const sw = await navigator.serviceWorker.ready;
  const token = await _props.oidcClient.token();

  sw.active.postMessage({
    type: 'app-load',
    token: token,
    settings: _props.settings
  });

  // Ensure periodic token refresh starts after app-load succeeds
  await scheduleNextTokenRefresh();
}

// Periodically refresh the IDP token and push it to the service worker
async function sendTokenRefreshEvent(): Promise<void> {
    if (!_props) { return; }
  try {
    const sw = await navigator.serviceWorker.ready;
    const freshToken = await _props.oidcClient.token();
    sw.active?.postMessage({
      type: 'token-refresh',
      token: freshToken,
    });
    window.debugConsoleLog?.(`Token refreshed and sent to ServiceWorker`);
    // After refreshing, schedule the next refresh based on new token expiration
    await scheduleNextTokenRefresh();
  } catch (ex) {
    console.error('Failed to refresh token', ex);
    window.debugConsoleLog?.(`Failed to refresh token: ${ex}`, 'error');
  }
}

// PWA setup
const pwaPath = isEndpoint('/pwa');
const uninstallPath = isEndpoint('/uninstall');

const isRunningAsPwa = runningAsPwa();
const isShouldRunAsPwa = shouldRunAsPwa();

let pwaModulesCached: Promise<void> = null;

if (pwaPath || isShouldRunAsPwa || isRunningAsPwa) {
  console.log(`Initializing PWA`)

  window.debugConsoleLog?.(`Initializing PWA`);

  registerServiceWorker();
  addManifest();

  let resolvePwaModulesCached: () => void;
  pwaModulesCached = new Promise((resolve) => {
    resolvePwaModulesCached = resolve;
  });

  // listen for messages from the service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    const { data } = event;
    if (data.type === 'activated') {
      const instanceId = data.data.instanceId;
      console.log(`PWA updated with instanceId: ${instanceId}`);
      sendAppLoadEvent();
    }
    else if (data.type === 'preload') {
      resolvePwaModulesCached();
      // Ensure the token refresh is scheduled once preload finishes
      clearTokenRefreshTimeout();
      scheduleNextTokenRefresh();
    }
    else if (data.type === 'debug-console-log') {
      const { message, level } = data;
      if (window.debugConsoleLog) {
        window.debugConsoleLog(message, level);
      }
    }
  });
}

let shouldLoadApp = true;

// Check for uninstall path first
if (uninstallPath) {
  console.log("Loading PWA uninstaller screen");
  
  // render our PWAUninstaller
  root.render(<PWAUninstaller />);
  
  // we don't need to continue the app initialization;
  shouldLoadApp = false;
}
else if (isRunningAsPwa
  && window.location.pathname === '/'
  && window.location.search === ''
  && !localStorage.getItem("url_state")) {
  console.log(`Loading PWA under construction screen (${window.location.href})`);

  // PWA opened directly rather than through deeplink, show under construction
  root.render(<PWAUnderConstruction />);

  // we don't need to continue the app initialization;
  shouldLoadApp = false;
}
else if (pwaPath) {
  // lets check if our PWA is already installed
  const isInstalled = isRunningAsPwa;

  if (!isInstalled) {

    console.log("Loading PWA install screen");

    // render our PWAInstaller
    root.render(<PWAInstaller />);

    // we don't need to continue the app initialization;
    shouldLoadApp = false;
  }
  else {
    console.log("PWA already installed");
  }
}
else if (!isRunningAsPwa && isShouldRunAsPwa) {
  window.location.href = '/pwa';
}

if (shouldLoadApp) {
  const appModule = await import('../app/core');

  window.debugConsoleLog?.(`Initializing App-Shell`);

  if (isRunningAsPwa) {
    await appModule.runAppShell(root, true, (
      async (client, settings) => {

        await sendAppLoadEvent({ oidcClient: client, settings: settings });

        // wait for the service worker to pre-cache the modules now
        await pwaModulesCached
      }));
  }
  else {
    await appModule.runAppShell(root, false);
  }

}